const BOUND_ATTR = /^data-.*bound$/i;

/** Capture le markup initial de chaque `.view` avant le premier contrôleur. */
export function snapshotViews(root: ParentNode): Map<string, string> {
  const snapshots = new Map<string, string>();
  root.querySelectorAll<HTMLElement>('.view[id]').forEach((section) => {
    snapshots.set(section.id, section.innerHTML);
  });
  return snapshots;
}

/** Retire les modales de formulaire, en laissant les confirmations ouvertes. */
export function dismissFormModals(root: ParentNode): void {
  root.querySelectorAll('nexxus-modal').forEach((modal) => {
    if (modal.hasAttribute('data-confirm')) return;
    modal.remove();
  });
}

/**
 * Remplace la section par un clone sans listeners, avec le markup d'origine.
 * Les drapeaux `data-*-bound` sont effacés pour que le contrôleur se rebranche.
 */
export function resetViewSection(section: HTMLElement, snapshot: string): HTMLElement {
  dismissFormModals(section);
  const fresh = section.cloneNode(false) as HTMLElement;
  for (const attr of Array.from(fresh.attributes)) {
    if (BOUND_ATTR.test(attr.name)) fresh.removeAttribute(attr.name);
  }
  fresh.innerHTML = snapshot;
  section.replaceWith(fresh);
  return fresh;
}

/**
 * Remet toutes les `.view` au markup initial (C2 / switch dossier).
 * Évite qu’un écran masqué garde encore les salariés / bulletins du dossier précédent.
 */
export function purgeAllViewSections(
  root: ParentNode,
  snapshots: Map<string, string>,
): void {
  root.querySelectorAll<HTMLElement>('.view[id]').forEach((section) => {
    const snapshot = snapshots.get(section.id);
    if (snapshot === undefined) return;
    resetViewSection(section, snapshot);
  });
}

const exclusive = new WeakMap<EventTarget, Map<string, EventListenerOrEventListenerObject>>();

function exclusiveKey(type: string, options?: AddEventListenerOptions): string {
  return `${type}:${options?.capture ? '1' : '0'}`;
}

/** Un seul listener de ce type par nœud : le précédent est retiré. */
export function onExclusive(
  target: EventTarget | null,
  type: string,
  handler: EventListenerOrEventListenerObject,
  options?: AddEventListenerOptions,
): void {
  if (!target) return;
  const key = exclusiveKey(type, options);
  let byType = exclusive.get(target);
  if (!byType) {
    byType = new Map();
    exclusive.set(target, byType);
  }
  const prev = byType.get(key);
  if (prev) target.removeEventListener(type, prev, options);
  byType.set(key, handler);
  target.addEventListener(type, handler, options);
}
